import React, { useMemo, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { camelCase } from 'lodash';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TableBody, TableContainer, Table, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import { AppSnackbar } from '@sierralabs/core-ui';
import { useEntityContext } from '../EntityContext';
import { useCreateEntityData } from '../hooks/useCreateEntityData';
import { useUpdateEntityData } from '../hooks/useUpdateEntityData';
import { EntityRelationField, isRelationColumn } from '../common/EntityRelationField';
const StyledTableRow = styled(TableRow) `
  .MuiTableCell-root {
    vertical-align: top;
  }
`;
const EditEntityDataDialog = ({ entityData, entity, open, onCancel, onSave }) => {
    const { plugins } = useEntityContext();
    const onSaveRef = useRef(onSave);
    // const [data, setData] = useState<EntityCreateColumn[]>([]);
    const primaryCol = entity.columns.find(column => column.primary);
    const dataId = primaryCol &&
        (entityData === null || entityData === void 0 ? void 0 : entityData[primaryCol.name || camelCase(primaryCol.displayName)]);
    const { updatedEntityData, isUpdatingData, error: updateError, updateEntityData } = useUpdateEntityData(entity.tableName, dataId);
    const { createdEntityData, isCreatingData, error, createEntityData } = useCreateEntityData(entity.tableName);
    // Initialize entity column data configuration
    const [entityColumnValues, setEntityColumnValues] = useState(() => entity.columns.map(column => {
        const columnType = column.type === 'int' ? 'integer' : column.type;
        return {
            column,
            plugin: plugins.find(p => p.type === columnType),
            value: entityData ? entityData[column.name] : undefined
        };
    }));
    // Check if entity column values are valid
    const isValid = useMemo(() => {
        return entityColumnValues.every(({ column, plugin, value }) => {
            var _a, _b;
            return !((_b = (_a = plugin === null || plugin === void 0 ? void 0 : plugin.edit) === null || _a === void 0 ? void 0 : _a.validateError) === null || _b === void 0 ? void 0 : _b.call(_a, column, value));
        });
    }, [entityColumnValues]);
    // After entity data is created close dialog
    useEffect(() => {
        var _a, _b;
        if (createdEntityData) {
            (_a = onSaveRef.current) === null || _a === void 0 ? void 0 : _a.call(onSaveRef, createdEntityData);
        }
        else if (updatedEntityData) {
            (_b = onSaveRef.current) === null || _b === void 0 ? void 0 : _b.call(onSaveRef, updatedEntityData);
        }
    }, [createdEntityData, updatedEntityData]);
    return (React.createElement(Dialog, { fullWidth: true, open: open, onClose: onCancel },
        React.createElement(DialogTitle, null, `${entityData ? 'Edit' : 'Add'} ${entity.displayName} Data`),
        React.createElement(DialogContent, { style: { paddingLeft: 0, paddingRight: 0 } },
            React.createElement(TableContainer, null,
                React.createElement(Table, null,
                    React.createElement(TableHead, null,
                        React.createElement(TableRow, null,
                            React.createElement(TableCell, null, "Column"),
                            React.createElement(TableCell, null, "Type"),
                            React.createElement(TableCell, null, "Data"))),
                    React.createElement(TableBody, null, entityColumnValues.map(({ column, plugin, value }, index) => {
                        var _a;
                        const EntityField = isRelationColumn(entity, column)
                            ? EntityRelationField
                            : (_a = plugin === null || plugin === void 0 ? void 0 : plugin.edit) === null || _a === void 0 ? void 0 : _a.field;
                        return (React.createElement(StyledTableRow, { key: `${column.name}-${index}` },
                            React.createElement(TableCell, null, column.displayName),
                            React.createElement(TableCell, null, column.type),
                            React.createElement(TableCell, null, column.generated ? (React.createElement(Typography, { color: 'textSecondary' }, value || 'Auto generated field')) : EntityField && plugin ? (React.createElement(EntityField, { plugin: plugin, column: column, entity: entity, value: value, onChange: value => setEntityColumnValues(values => {
                                    values[index].value = value;
                                    return [...values];
                                }) })) : (React.createElement(Typography, { color: 'textSecondary' }, "No options for this type.")))));
                    }))))),
        React.createElement(DialogActions, null,
            React.createElement(Button, { onClick: () => {
                    onCancel === null || onCancel === void 0 ? void 0 : onCancel();
                } }, "Cancel"),
            React.createElement(Button, { variant: 'contained', color: 'primary', disabled: !isValid || isCreatingData || isUpdatingData, onClick: () => {
                    const dataValue = {};
                    for (const columnValue of entityColumnValues) {
                        dataValue[columnValue.column.name] = columnValue.value;
                    }
                    if (entityData) {
                        updateEntityData(dataValue);
                    }
                    else {
                        createEntityData(dataValue);
                    }
                } }, "Save")),
        React.createElement(AppSnackbar, { open: !!error || !!updateError, severity: 'error' }, (error === null || error === void 0 ? void 0 : error.message) || (updateError === null || updateError === void 0 ? void 0 : updateError.message))));
};
export default EditEntityDataDialog;
//# sourceMappingURL=EditEntityDataDialog.js.map